// Hidden things (issue #133): a few small things a scene hides and draws itself (a mouse on a shelf, a balloon that
// shows for a while), each a sticker in the city's passport once a tap finds it. A scene exports `secrets`
// (docs/SCENE_GUIDE.md "Hidden things"): [{ id, name, x, y, w, h, at, art }], a box in scene pixels round the thing,
// `at` the loop seconds it shows ([[from, to], …]; left out: all loop long) and `art` its sticker, 12 rows of 12
// letters in the style icons' palette. A tap on one answers like a tap spot (site/js/ui/spots.js: the same hit-test,
// the `secret` kind's sound and light) and, the first time, is kept: `lofi-cities:finds`, { tokyo: 'bozu ufo', … },
// the ids found per city. Signed in, a copy of them is a synced setting, `secrets` (site/js/live/account.js), where
// the newest change wins: so a device asks the account first, keeps what arrives together with its own, and only
// then sends the union (createFinds). Nothing hints at a hidden thing before it is found, until the city has its
// 5 minutes (secretHint).
// Pure but for the storage it's handed, so node can test it: node --test tests/secrets.test.mjs
import { LOOP } from '../engine/core.js';
import { SECRET, spotsOf } from './spots.js';
import { STAMP_S } from './passport.js';
import { PALETTE } from './style-icons.js';

export const ART = 12;                             // a sticker is ART x ART pixels
export const PER_CITY = 8;                         // ids kept per city, at most (a scene hides three)
export const SECRETS_ROOM = 1024;                  // the stored finds' JSON an account keeps (worker/account.js SETTING_MAX)
export const RESEND_MS = 60000;                    // the same union goes back to the account at most this often

const ID = /^[a-z][a-z0-9]{1,7}$/;                 // 'ufo', 'balloon': short, they are stored and synced
const CITY = /^[a-z][a-z0-9-]{0,31}$/;             // as the account takes a city's id

/* ---------------- what a scene hides ---------------- */
const whole = (...n) => n.every(Number.isInteger);
const windowsOf = at => (at === undefined ? null
  : Array.isArray(at) && at.length && at.every(w => Array.isArray(w) && w.length === 2 && w[0] >= 0 && w[0] < w[1] && w[1] <= LOOP) ? at.map(([a, b]) => [a, b]) : false);
const artOf = art => (Array.isArray(art) && art.length === ART && art.every(row => typeof row === 'string' && row.length === ART) ? art : null);
const kept = new WeakMap();
/**
 * The hidden things of `scene` this page can use, each { id, name, x, y, w, h, at (null: all loop long), art, kind }:
 * a tab left open across a deploy, or a scene written wrong, may bring one it can't (left out, as is a second one
 * with an id already taken).
 */
export function secretsOf(scene) {
  if (!scene || !Array.isArray(scene.secrets)) return [];
  if (!kept.has(scene)) {
    const list = [];
    for (const s of scene.secrets) {
      const at = windowsOf(s?.at), art = artOf(s?.art);
      if (!s || !ID.test(s.id) || list.some(q => q.id === s.id) || typeof s.name !== 'string' || !s.name.trim() || at === false || !art) continue;
      if (!whole(s.x, s.y, s.w, s.h) || s.w <= 0 || s.h <= 0) continue;
      list.push({ id: s.id, name: s.name.trim(), x: s.x, y: s.y, w: s.w, h: s.h, at, art, kind: SECRET });
    }
    kept.set(scene, list);
  }
  return kept.get(scene);
}
/** A city's hidden things: those of its looks' scenes (the ones loaded so far), an id once. */
export const citySecrets = scenes => scenes.flatMap(secretsOf).filter((s, i, all) => all.findIndex(q => q.id === s.id) === i);

/** Whether `secret` is on show at loop second `t`: only then can it be found. */
export const showing = (secret, t) => !secret.at || secret.at.some(([a, b]) => t >= a && t < b);

/**
 * What a tap can land on in `scene` at loop second `t` (spots.js hitSpot takes the list): its hidden things on show
 * then, or at `t0` when the press began (a finger that went down on one in its last moment still has it), ahead of
 * its tap spots. `only(secret)` keeps some of them: a mouse over the scene gets the hand for a found one alone.
 */
export const tappable = (scene, t, t0 = t, only = null) => [
  ...secretsOf(scene).filter(s => (showing(s, t) || showing(s, t0)) && (!only || only(s))), ...spotsOf(scene),
];

/* ---------------- finds: the ids found per city ---------------- */
/** Stored finds, well-formed: { city: [id, …] }, the cities and each one's ids in order, anything else left out. */
export function findsOf(raw) {
  const out = {};
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return out;
  for (const city of Object.keys(raw).sort()) {
    const v = raw[city], ids = typeof v === 'string' ? v.split(' ') : Array.isArray(v) ? v : [];
    const clean = [...new Set(ids.filter(id => typeof id === 'string' && ID.test(id)))].sort().slice(0, PER_CITY);
    if (CITY.test(city) && clean.length) out[city] = clean;
  }
  return out;
}
/** Finds as they are stored and synced: { city: 'id id' }. Two devices with the same finds store the same text. */
export const packFinds = finds => Object.fromEntries(Object.entries(findsOf(finds)).map(([city, ids]) => [city, ids.join(' ')]));

/**
 * Two devices' finds as one: { finds: everything either has, added: `b` brought something `a` lacked, more: `a` has
 * something `b` lacks }.
 */
export function mergeFinds(a, b) {
  const A = findsOf(a), B = findsOf(b), both = {};
  let added = false, more = false;
  for (const city of new Set([...Object.keys(A), ...Object.keys(B)])) {
    const mine = A[city] ?? [], theirs = B[city] ?? [];
    both[city] = [...mine, ...theirs];
    if (theirs.some(id => !mine.includes(id))) added = true;
    if (mine.some(id => !theirs.includes(id))) more = true;
  }
  return { finds: findsOf(both), added, more };
}

/** The things of `secrets` (a city's, secretsOf) found in `city`: one that left the scene, or a city that left the site, counts nowhere. */
export const foundOf = (finds, city, secrets) => secrets.filter(s => finds[city]?.includes(s.id));

/** A city's line in the Passport view: "2 of 3 found". */
export const foundLine = (found, total) => `${found} of ${total} found`;
/** Under it while `left` are still to find: where they are (the "?" tiles are no buttons), else ''. */
export const whereLine = left => (left > 1 ? 'tap them in the scene' : left === 1 ? 'tap it in the scene' : '');

/**
 * The hint: how many of a city's `total` hidden things are left to find, once it has `stampS` of listening (its
 * stamp's 5 minutes) and not all are `found`; else null. Before that nothing says a city hides anything.
 */
export const secretHint = (seconds, found, total, stampS = STAMP_S) => (total > found && seconds >= stampS ? total - found : null);

/**
 * This browser's finds. `load()` and `save(packed)` are their storage (`lofi-cities:finds`; either may throw, or load
 * junk): what this tab has seen stays if the storage loses it, `reload()` takes in what is stored by now (another
 * tab's finds), and so does every find before it is stored, so two tabs add up.
 *
 * `sent()` and `send(packed)` are the copy an account syncs (`lofi-cities:secrets`, a setting like any other: the
 * page's store tells the account when it is stored, and the account keeps its newest change). The finds themselves
 * are never that setting, so a find made here replaces nothing there: the page first syncs (the copy, with its old
 * time, loses to whatever the account has, which comes back through `take`), and only then calls `keep()`, which
 * stores the copy with both devices' finds in it. `due` says a keep is owed.
 */
export function createFinds({ load, save, sent = () => null, send = () => {} }) {
  let seen = {}, back = null;                       // back: the union last sent back to an account that had less, and when
  const read = fn => { try { return fn(); } catch { return null; } };
  const reload = () => (seen = mergeFinds(seen, read(load)).finds);
  const store = () => { try { save(packFinds(seen)); } catch { /* storage unavailable: this tab still knows */ } };
  const keep = () => { try { send(packFinds(seen)); } catch { /* the next keep sends it */ } };
  reload();
  return {
    /** The finds as this tab last read them, { city: [id, …] }. */
    get view() { return seen; },
    has: (city, id) => !!seen[city]?.includes(id),
    reload,
    /** `id` found in `city`: true the first time (it is stored), false when it was found before. */
    add(city, id) {
      const now = mergeFinds(reload(), { [city]: [id] });
      if (!now.added) return false;
      seen = now.finds;
      store();
      return true;
    },
    /** Whether this device has finds its copy for the account lacks. */
    get due() { return JSON.stringify(packFinds(seen)) !== JSON.stringify(packFinds(read(sent))); },
    /**
     * The account's finds arriving (`raw`, another device's; `now` in ms): kept with this device's. Where the account
     * has all of this device's, the copy becomes the account's own and there is nothing to send. Else true: this
     * device has some the account lacks and should `keep()` once the sync is done, but not the same union twice
     * within RESEND_MS, so an account that won't take it (a device whose clock runs ahead wrote last) is asked again
     * later, not in a loop.
     */
    take(raw, now) {
      const m = mergeFinds(reload(), raw);
      seen = m.finds;
      if (m.added) store();
      if (!m.more) { keep(); return false; }
      const text = JSON.stringify(packFinds(seen));
      if (back && back.text === text && now - back.at < RESEND_MS) return false;
      back = { text, at: now };
      return true;
    },
    /** Store the copy for the account as the finds are now. */
    keep,
  };
}

/* ---------------- stickers ---------------- */
// A hidden thing not found yet, once the city hints at it: the same tile for each.
export const UNFOUND = [
  '............', '............', '....SSSS....', '...SS..SS...', '.......SS...', '......SS....',
  '.....SS.....', '.....SS.....', '............', '.....SS.....', '.....SS.....', '............',
];
/** Paint a sticker (`art`: ART rows of ART letters, '.' clear, the others site/js/ui/style-icons.js PALETTE colours) into a canvas ART pixels square. */
export function paintSticker(canvas, art) {
  const g = canvas.getContext('2d');
  if (!g) return;
  g.clearRect(0, 0, canvas.width, canvas.height);
  art.forEach((row, y) => [...row].forEach((ch, x) => {
    if (!PALETTE[ch]) return;
    g.fillStyle = PALETTE[ch];
    g.fillRect(x, y, 1, 1);
  }));
}
